import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getUserPermissions,
  getAllUsersPermissions,
  getAvailableMenuItems,
  updateUserPermissions,
  resetUserPermissions,
  bulkUpdatePermissions,
} from "../../api";

// In-flight dedupe promises
let fetchUserPermissionsInFlight = null;

// Async Thunks
export const fetchUserPermissions = createAsyncThunk(
  "permissions/fetchUserPermissions",
  async (userId = "me", thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const isAuthenticated = state.auth?.isAuthenticated;
      const cached = state.permissions.currentUserPermissions;
      const fetchedAt = state.permissions.fetchedAt || 0;
      const isFresh = Date.now() - fetchedAt < 5 * 60 * 1000; // 5 minutes

      // Gate: only fetch if authenticated and data not present or stale
      if (!isAuthenticated && cached) {
        return cached;
      }
      if (isAuthenticated && cached && isFresh && userId === 'me') {
        return cached;
      }

      if (fetchUserPermissionsInFlight) {
        return await fetchUserPermissionsInFlight;
      }
      fetchUserPermissionsInFlight = getUserPermissions(userId).then(r => r.data);
      const data = await fetchUserPermissionsInFlight;
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to fetch user permissions"
      );
    } finally {
      fetchUserPermissionsInFlight = null;
    }
  }
);

export const fetchAllUsersPermissions = createAsyncThunk(
  "permissions/fetchAllUsersPermissions",
  async (_, thunkAPI) => {
    try {
      const data = await getAllUsersPermissions();
      
      // Get the users from the auth state
      const state = thunkAPI.getState();
      const users = state.auth.users;

      // Process each user's permissions
      const processedData = data.data.map(userPerm => {
        // If user has no permissions, create default structure based on role
        if (!userPerm.permissions || !userPerm.permissions.menuPermissions) {
          const user = users.find(u => u._id === userPerm.userId);
          if (user) {
            return {
              ...userPerm,
              permissions: {
                menuPermissions: [],
                lastModified: new Date().toISOString(),
                assignedBy: 'system',
                isActive: true
              }
            };
          }
        }
        return userPerm;
      });

      return processedData;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to fetch all users permissions"
      );
    }
  }
);

export const fetchAvailableMenuItems = createAsyncThunk(
  "permissions/fetchAvailableMenuItems",
  async (_, thunkAPI) => {
    try {
      const data = await getAvailableMenuItems();
      return data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to fetch menu items"
      );
    }
  }
);

export const updateUserPerms = createAsyncThunk(
  "permissions/updateUserPerms",
  async ({ userId, menuPermissions }, thunkAPI) => {
    try {
      const data = await updateUserPermissions(userId, menuPermissions);
      return data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to update user permissions"
      );
    }
  }
);

export const resetUserPerms = createAsyncThunk(
  "permissions/resetUserPerms",
  async (userId, thunkAPI) => {
    try {
      const data = await resetUserPermissions(userId);
      return data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to reset user permissions"
      );
    }
  }
);

export const bulkUpdatePerms = createAsyncThunk(
  "permissions/bulkUpdatePerms",
  async ({ userIds, menuPermissions }, thunkAPI) => {
    try {
      const data = await bulkUpdatePermissions(userIds, menuPermissions);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to bulk update permissions"
      );
    }
  }
);

// Initial state
const initialState = {
  currentUserPermissions: null,
  allUsersPermissions: [],
  availableMenuItems: [],
  loading: false,
  error: null,
  fetchedAt: null,
};

// Slice
const permissionSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    clearPermissionsState: (state) => {
      state.currentUserPermissions = null;
      state.allUsersPermissions = [];
      state.availableMenuItems = [];
      state.error = null;
      state.fetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUserPermissions = action.payload;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllUsersPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsersPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.allUsersPermissions = action.payload;
      })

      .addCase(fetchAllUsersPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAvailableMenuItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableMenuItems.fulfilled, (state, action) => {
        state.loading = false;
        state.availableMenuItems = action.payload;
      })
      .addCase(fetchAvailableMenuItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateUserPerms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(updateUserPerms.fulfilled, (state, action) => {
        state.loading = false;
        const updatedUser = action.payload;

        // Update allUsersPermissions array
        const index = state.allUsersPermissions.findIndex(
          (u) => u.userId === updatedUser.userId
        );
        if (index !== -1) {
          state.allUsersPermissions[index].permissions = {
            menuPermissions: updatedUser.menuPermissions,
            lastModified: updatedUser.lastModified,
            assignedBy: updatedUser.assignedBy,
            isActive: updatedUser.isActive,
          };
        }

        // Update currentUserPermissions if it's the current user
        if (
          state.currentUserPermissions &&
          state.currentUserPermissions.userId === updatedUser.userId
        ) {
          state.currentUserPermissions = {
            ...state.currentUserPermissions,
            menuPermissions: updatedUser.menuPermissions,
            lastModified: updatedUser.lastModified,
            assignedBy: updatedUser.assignedBy,
            isActive: updatedUser.isActive,
          };
        }
      })
      .addCase(updateUserPerms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(resetUserPerms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetUserPerms.fulfilled, (state, action) => {
        state.loading = false;
        const updatedUser = action.payload;

        // Update allUsersPermissions array
        const index = state.allUsersPermissions.findIndex(
          (u) => u.userId === updatedUser.userId
        );
        if (index !== -1) {
          state.allUsersPermissions[index].permissions = {
            menuPermissions: updatedUser.menuPermissions,
            lastModified: updatedUser.lastModified,
            isActive: updatedUser.isActive,
          };
        }

        // Update currentUserPermissions if it's the current user
        if (
          state.currentUserPermissions &&
          state.currentUserPermissions.userId === updatedUser.userId
        ) {
          state.currentUserPermissions = {
            ...state.currentUserPermissions,
            menuPermissions: updatedUser.menuPermissions,
            lastModified: updatedUser.lastModified,
            isActive: updatedUser.isActive,
          };
        }
      })

      .addCase(resetUserPerms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(bulkUpdatePerms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdatePerms.fulfilled, (state, action) => {
        const { userIds, menuPermissions } = action.meta.arg;
        const now = new Date().toISOString();

        userIds.forEach((userId) => {
          const index = state.allUsersPermissions.findIndex(
            (u) => u.userId === userId
          );
          if (index !== -1) {
            state.allUsersPermissions[index].permissions = {
              ...(state.allUsersPermissions[index].permissions || {}),
              menuPermissions,
              lastModified: now,
              isActive: true,
              assignedBy: "admin",
            };
          }
        });

        state.loading = false;
        state.error = null;
      })
      .addCase(bulkUpdatePerms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearPermissionsState } = permissionSlice.actions;
export default permissionSlice.reducer;
